import { useNaphthaleneStore, NaphthaleneStage } from '../store/useNaphthaleneStore';
import {
  Beaker as BeakerIcon,
  Thermometer,
  Droplets,
  ArrowDownToLine,
  Flame,
  Pause,
  Save,
  RotateCcw,
  ArrowRightLeft,
  AlertTriangle,
} from 'lucide-react';

const STAGE_TO_STEP: Record<NaphthaleneStage, number> = {
  'setup': 1,
  'naphthalene-added': 2,
  'thermometer-inserted': 3,
  'water-poured': 4,
  'tube-immersed': 5,
  'heating': 5,
  'melting': 6,
  'transfer-ready': 7,
  'transferring': 7,
  'cooling': 8,
  'freezing': 8,
  'completed': 8,
};

export function NaphthaleneProcedure() {
  const labStage = useNaphthaleneStore(s => s.labStage);
  const isHeating = useNaphthaleneStore(s => s.isHeating);
  const isStirring = useNaphthaleneStore(s => s.isStirring);
  const experimentTime = useNaphthaleneStore(s => s.experimentTime);
  const currentTemperature = useNaphthaleneStore(s => s.currentTemperature);
  const meltFraction = useNaphthaleneStore(s => s.meltFraction);

  const isAddingNaphthalene = useNaphthaleneStore(s => s.isAddingNaphthalene);
  const isInsertingThermometer = useNaphthaleneStore(s => s.isInsertingThermometer);
  const isPouringWater = useNaphthaleneStore(s => s.isPouringWater);
  const isImmersingTube = useNaphthaleneStore(s => s.isImmersingTube);
  const isTransferring = useNaphthaleneStore(s => s.isTransferring);

  const addNaphthaleneToTube = useNaphthaleneStore(s => s.addNaphthaleneToTube);
  const insertThermometer = useNaphthaleneStore(s => s.insertThermometer);
  const pourWater = useNaphthaleneStore(s => s.pourWater);
  const immerseTube = useNaphthaleneStore(s => s.immerseTube);
  const toggleHeating = useNaphthaleneStore(s => s.toggleHeating);
  const setStirring = useNaphthaleneStore(s => s.setStirring);
  const recordReading = useNaphthaleneStore(s => s.recordReading);
  const transferToFlask = useNaphthaleneStore(s => s.transferToFlask);
  const reset = useNaphthaleneStore(s => s.reset);

  const animating =
    isAddingNaphthalene || isInsertingThermometer || isPouringWater ||
    isImmersingTube || isTransferring;

  const stepNumber = STAGE_TO_STEP[labStage];

  const matterState = meltFraction >= 1 ? 'Liquid' : meltFraction > 0 ? 'Solid+Liquid' : 'Solid';

  const stirringEnabled =
    labStage === 'heating' || labStage === 'melting' ||
    labStage === 'cooling' || labStage === 'freezing';

  const heatingEnabled =
    labStage === 'tube-immersed' || labStage === 'heating' || labStage === 'melting';

  const recordEnabled =
    labStage === 'heating' || labStage === 'melting' ||
    labStage === 'transfer-ready' || labStage === 'cooling' ||
    labStage === 'freezing';

  return (
    <div className="flex flex-col h-full pointer-events-auto">
      <div className="pb-4 mb-4 border-b border-border/50">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent flex items-center gap-2">
              Melting Point of Naphthalene
            </h2>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
              <Thermometer className="w-3 h-3" /> Step {stepNumber} / 8 — observe heating &amp; cooling plateaus
            </p>
          </div>
          <div className="px-2.5 py-1 rounded-lg bg-orange-500/10 border border-orange-500/30 text-orange-500 text-xs font-bold uppercase tracking-wider">
            {labStage.replace('-', ' ')}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
        <div className="mb-4 p-3 rounded-lg bg-card border border-border space-y-2 text-sm shadow-inner">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Time</span>
            <span className="font-mono text-blue-500">{Math.floor(experimentTime)}s</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Naphthalene</span>
            <span className="font-mono text-orange-500">{currentTemperature.toFixed(1)}°C — {matterState}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Melt Fraction</span>
            <span className="font-mono">{(meltFraction * 100).toFixed(0)}%</span>
          </div>
        </div>

        <div className="space-y-2.5">
          <button
            disabled={labStage !== 'setup' || animating}
            onClick={addNaphthaleneToTube}
            className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-cyan-500/20"
          >
            <BeakerIcon className="w-4 h-4" />
            {isAddingNaphthalene ? 'Adding naphthalene…' : '1. Add Naphthalene to Boiling Tube'}
          </button>

          <button
            disabled={labStage !== 'naphthalene-added' || animating}
            onClick={insertThermometer}
            className="w-full py-3 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-indigo-500/20"
          >
            <Thermometer className="w-4 h-4" />
            {isInsertingThermometer ? 'Inserting thermometer…' : '2. Insert Thermometer'}
          </button>

          <button
            disabled={labStage !== 'thermometer-inserted' || animating}
            onClick={pourWater}
            className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-sky-500/20"
          >
            <Droplets className="w-4 h-4" />
            {isPouringWater ? 'Pouring water…' : '3. Pour Water into Beaker'}
          </button>

          <button
            disabled={labStage !== 'water-poured' || animating}
            onClick={immerseTube}
            className="w-full py-3 rounded-xl bg-violet-500 hover:bg-violet-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-violet-500/20"
          >
            <ArrowDownToLine className="w-4 h-4" />
            {isImmersingTube ? 'Immersing tube…' : '4. Immerse Boiling Tube'}
          </button>

          <button
            disabled={!heatingEnabled || animating}
            onClick={toggleHeating}
            className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              isHeating
                ? 'bg-red-500/10 text-red-500 border border-red-500/50 hover:bg-red-500/20'
                : 'bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-500/20'
            }`}
          >
            {isHeating ? <><Pause className="w-4 h-4" /> Stop Bunsen</> : <><Flame className="w-4 h-4" /> 5. Ignite Bunsen Burner</>}
          </button>

          <button
            disabled={!stirringEnabled || animating}
            onClick={() => setStirring(!isStirring)}
            className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              isStirring
                ? 'bg-blue-500 text-white shadow-md shadow-blue-500/20'
                : 'bg-muted hover:bg-muted/80 text-foreground border border-border'
            }`}
          >
            <Droplets className="w-4 h-4" />
            {isStirring ? 'Stirring…' : '6. Stir Continuously'}
          </button>

          <button
            disabled={!recordEnabled || animating}
            onClick={recordReading}
            className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-emerald-500/20"
          >
            <Save className="w-4 h-4" /> Record Temperature
          </button>

          {labStage === 'transfer-ready' && (
            <button
              onClick={transferToFlask}
              disabled={animating}
              className="w-full py-3 rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-teal-500/30 animate-pulse"
            >
              <ArrowRightLeft className="w-4 h-4" />
              7. Transfer to Conical Flask &amp; Cool
            </button>
          )}

          {labStage === 'completed' && (
            <button
              onClick={reset}
              className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-medium flex items-center justify-center gap-2 shadow-md shadow-amber-500/20"
            >
              <RotateCcw className="w-4 h-4" /> Restart Experiment
            </button>
          )}
        </div>

        <div className="mt-6 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <h4 className="text-amber-600 dark:text-amber-400 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Safety
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1.5 list-disc list-inside ml-2">
            <li>Do not touch naphthalene directly — it is harmful on contact.</li>
            <li>Naphthalene vapour is toxic — keep the experiment ventilated.</li>
            <li>Heat indirectly via a water bath; never put naphthalene over an open flame.</li>
          </ul>
        </div>

        <div className="mt-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h4 className="text-blue-600 dark:text-blue-400 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
            <Flame className="w-4 h-4" /> Tips
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1.5 list-disc list-inside ml-2">
            <li>Watch for the temperature plateau (~80°C) during melting and freezing.</li>
            <li>Stir continuously while cooling to avoid supercooling.</li>
            <li>Record a reading every 30 seconds for the heating/cooling curve.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
